import { type CalendarSettings, CalendarSettingsCreateSchema, type Holiday, HolidayCreateSchema } from '@gestion-permisos/contracts';
import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useCreateCalendarSettings,
  useCreateHoliday,
  useCurrentCalendarSettings,
  useHolidayYear,
  useToggleHoliday,
} from '../admin/admin-api.js';
import { BackToAdmin, describedBy, Field, firstForbidden, INPUT, Notice } from '../admin/admin-ui.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CardTitle } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { formatCivilDate } from '../requests/format.js';
import { TABLE, TD, TH } from '../requests/request-list.js';

const DEFAULT_TIME_ZONE = 'America/Bogota';
/** Año de cuatro dígitos, como texto (la API admite de 2000 a 2100 y responde 400 si no). */
const YEAR = /^\d{4}$/;

/** Botones de esta pantalla, con el aspecto de las ya adaptadas (`admin-ui.tsx` conserva los suyos). */
const PRIMARY = buttonClasses();
const OUTLINE = buttonClasses('outline');
/** Cabecera de una tarjeta: el título a la izquierda y su botón a la derecha. */
const CARD_HEAD = 'flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4';

/**
 * Calendario (W7): la configuración vigente y una nueva con vigencia futura (`POST /calendar/settings`), y los
 * festivos de un año (`GET /calendar/holidays?year=`), con alta manual, desactivación y reactivación. Fechas
 * y horas como texto, sin `Date`. **El cliente no calcula festivos**: los calcula y ajusta la API. Si la
 * vigencia no es futura, la API responde 422 y se muestra; aquí no se rechaza.
 *
 * Con el aspecto de las demás pantallas de administración: cabecera, la configuración y los festivos en
 * tarjetas, y las dos altas en ventanas (`Modal`), una a la vez. El año y activar o desactivar un festivo
 * siguen en la página. Cerrar una ventana no envía nada.
 */
export function AdminCalendarPage() {
  const [params, setParams] = useSearchParams();
  const yearParam = params.get('anio') ?? '';
  const year = YEAR.test(yearParam) ? yearParam : undefined;

  const settings = useCurrentCalendarSettings();
  const createSettings = useCreateCalendarSettings();
  const holidays = useHolidayYear(year);
  const createHoliday = useCreateHoliday();
  const toggle = useToggleHoliday();
  const [open, setOpen] = useState<'settings' | 'holiday' | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  // Un 403 sustituye la página entera, ventana incluida.
  const denied = firstForbidden(settings.error, createSettings.error, holidays.error, createHoliday.error, toggle.error);
  if (denied) return <ApiErrorView error={denied} />;

  const openWindow = (which: 'settings' | 'holiday') => {
    // Un error de un alta anterior no se arrastra a la nueva.
    if (which === 'settings') createSettings.reset();
    else createHoliday.reset();
    setSaved(undefined);
    setOpen(which);
  };
  const close = () => setOpen(undefined);

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Calendario" description="Jornada, zona horaria y festivos." />
      <Notice text={saved} />

      <Card flush>
        <div className={CARD_HEAD}>
          <CardTitle>Configuración vigente</CardTitle>
          <button type="button" className={OUTLINE} onClick={() => openWindow('settings')}>
            Nueva configuración
          </button>
        </div>
        <div className="p-5">
          {settings.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
          {/* Sin configuración: el error de la API (p. ej., CALENDAR_NOT_CONFIGURED); el botón de la cabecera abre la ventana. */}
          {settings.isError ? <ProblemMessage error={settings.error} /> : null}
          {settings.isSuccess ? (
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
              {(
                [
                  ['Vigente desde', formatCivilDate(settings.data.effectiveFrom)],
                  ['Zona horaria', settings.data.timeZone],
                  ['Jornada', `${settings.data.workdayStart}–${settings.data.workdayEnd}`],
                  ['Tramos', `${settings.data.slotMinutes} min`],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-sm text-muted-foreground">{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </Card>

      <Card flush>
        <div className={CARD_HEAD}>
          <CardTitle>Festivos</CardTitle>
          <button type="button" className={OUTLINE} onClick={() => openWindow('holiday')}>
            Añadir festivo
          </button>
        </div>
        <div className="space-y-3 p-5">
          <YearForm year={yearParam} onSearch={(next) => setParams(next ? { anio: next } : {})} />
          {yearParam !== '' && year === undefined ? <p className="text-sm text-red-700">Escribe un año de cuatro dígitos.</p> : null}
          {year === undefined ? <p className="text-sm text-muted-foreground">Indica un año para ver sus festivos.</p> : null}
          {year !== undefined && holidays.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
          {holidays.isError ? <ProblemMessage error={holidays.error} /> : null}
          {toggle.error ? <ProblemMessage error={toggle.error} /> : null}
          {holidays.isSuccess && holidays.data.length === 0 ? <p className="text-sm text-muted-foreground">No hay festivos en {year}.</p> : null}
        </div>
        {holidays.isSuccess && holidays.data.length > 0 ? (
          <div className="overflow-x-auto border-t border-border">
            <table className={TABLE} aria-label={`Festivos de ${year}`}>
              <thead className="border-b border-border bg-muted text-muted-foreground">
                <tr>
                  <th scope="col" className={TH}>Fecha</th>
                  <th scope="col" className={TH}>Nombre</th>
                  <th scope="col" className={TH}>Origen</th>
                  <th scope="col" className={TH}>Estado</th>
                  <th scope="col" className={TH}>
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {holidays.data.map((holiday) => (
                  <tr key={holiday.date} className="border-t border-border transition-colors first:border-t-0 hover:bg-muted">
                    <td className={TD}>{formatCivilDate(holiday.date)}</td>
                    <th scope="row" className={TH}>{holiday.name}</th>
                    <td className={TD}>{holiday.source === 'MANUAL' ? 'Manual' : 'Calculado'}</td>
                    <td className={TD}>{holiday.active ? 'Activo' : 'Anulado'}</td>
                    <td className={TD}>
                      <button
                        type="button"
                        className={OUTLINE}
                        disabled={toggle.isPending}
                        aria-label={`${holiday.active ? 'Desactivar' : 'Activar'} ${holiday.name}`}
                        onClick={() => toggle.mutate({ date: holiday.date, action: holiday.active ? 'disable' : 'enable' })}
                      >
                        {holiday.active ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {open === 'settings' ? (
        <Modal title="Nueva configuración" description="La vigencia tiene que ser futura. Si no lo es, la API la rechaza." onClose={close}>
          <SettingsForm
            create={createSettings}
            onCancel={close}
            onCreated={(created) => {
              close();
              setSaved(`Configuración registrada: vigente desde el ${formatCivilDate(created.effectiveFrom)}.`);
            }}
          />
        </Modal>
      ) : null}

      {open === 'holiday' ? (
        <Modal title="Añadir un festivo manual" onClose={close}>
          <HolidayForm
            create={createHoliday}
            onCancel={close}
            onCreated={(holiday) => {
              close();
              setSaved(`Festivo «${holiday.name}» añadido el ${formatCivilDate(holiday.date)}.`);
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

type SettingsErrors = Partial<Record<'effectiveFrom' | 'timeZone' | 'workdayStart' | 'workdayEnd' | 'slotMinutes', string>>;

const SETTINGS_MESSAGES: Required<SettingsErrors> = {
  effectiveFrom: 'Indica la fecha de vigencia (AAAA-MM-DD).',
  timeZone: 'Indica la zona horaria (p. ej., America/Bogota).',
  workdayStart: 'Indica la hora de inicio (HH:mm).',
  workdayEnd: 'Indica una hora de fin (HH:mm) posterior a la de inicio.',
  slotMinutes: 'Los tramos son de 5 a 240 minutos, en números enteros.',
};

function SettingsForm({
  create,
  onCancel,
  onCreated,
}: {
  create: ReturnType<typeof useCreateCalendarSettings>;
  onCancel: () => void;
  /** Tras el registro se cierra la ventana y la página muestra el aviso. */
  onCreated: (created: CalendarSettings) => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [workdayStart, setWorkdayStart] = useState('08:00');
  const [workdayEnd, setWorkdayEnd] = useState('17:30');
  const [slotMinutes, setSlotMinutes] = useState('30');
  const [errors, setErrors] = useState<SettingsErrors>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const slot = /^\d+$/.test(slotMinutes.trim()) ? Number(slotMinutes.trim()) : Number.NaN;
    const parsed = CalendarSettingsCreateSchema.safeParse({ effectiveFrom, timeZone: timeZone.trim(), workdayStart, workdayEnd, slotMinutes: slot });
    if (!parsed.success) {
      const next: SettingsErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && field in SETTINGS_MESSAGES) next[field as keyof SettingsErrors] = SETTINGS_MESSAGES[field as keyof SettingsErrors];
      }
      setErrors(next);
      return;
    }
    setErrors({});
    create.mutate(parsed.data, { onSuccess: onCreated });
  };

  const field = (id: keyof SettingsErrors) => ({ id: `cal-${id}`, error: errors[id] });
  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label="Nueva configuración">
      {/* 422 (vigencia anterior a hoy, zona no válida…): se muestra tal cual y la ventana sigue abierta. */}
      {create.error ? <ProblemMessage error={create.error} /> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field {...field('effectiveFrom')} label="Vigente desde">
          <input id="cal-effectiveFrom" type="date" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} className={INPUT} {...describedBy('cal-effectiveFrom', errors.effectiveFrom)} />
        </Field>
        <Field {...field('timeZone')} label="Zona horaria">
          <input id="cal-timeZone" value={timeZone} onChange={(event) => setTimeZone(event.target.value)} className={INPUT} {...describedBy('cal-timeZone', errors.timeZone)} />
        </Field>
        <Field {...field('workdayStart')} label="Inicio de la jornada">
          <input id="cal-workdayStart" type="time" value={workdayStart} onChange={(event) => setWorkdayStart(event.target.value)} className={INPUT} {...describedBy('cal-workdayStart', errors.workdayStart)} />
        </Field>
        <Field {...field('workdayEnd')} label="Fin de la jornada">
          <input id="cal-workdayEnd" type="time" value={workdayEnd} onChange={(event) => setWorkdayEnd(event.target.value)} className={INPUT} {...describedBy('cal-workdayEnd', errors.workdayEnd)} />
        </Field>
        <Field {...field('slotMinutes')} label="Minutos por tramo">
          <input id="cal-slotMinutes" inputMode="numeric" value={slotMinutes} onChange={(event) => setSlotMinutes(event.target.value)} className={INPUT} {...describedBy('cal-slotMinutes', errors.slotMinutes)} />
        </Field>
      </div>
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" disabled={create.isPending} className={PRIMARY}>
          Registrar configuración
        </button>
      </ModalFooter>
    </form>
  );
}

function YearForm({ year, onSearch }: { year: string; onSearch: (year: string) => void }) {
  const [value, setValue] = useState(year);
  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(value.trim());
      }}
    >
      <Field id="holiday-year" label="Año">
        <input id="holiday-year" inputMode="numeric" maxLength={4} value={value} onChange={(event) => setValue(event.target.value)} className={INPUT} />
      </Field>
      <button type="submit" className={OUTLINE}>
        Ver festivos
      </button>
    </form>
  );
}

function HolidayForm({
  create,
  onCancel,
  onCreated,
}: {
  create: ReturnType<typeof useCreateHoliday>;
  onCancel: () => void;
  /** Tras el alta se cierra la ventana y la página muestra el aviso. */
  onCreated: (holiday: Holiday) => void;
}) {
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<{ date?: string; name?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = HolidayCreateSchema.safeParse({ date, name });
    if (!parsed.success) {
      const next: { date?: string; name?: string } = {};
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'date') next.date = 'Indica la fecha (AAAA-MM-DD).';
        if (issue.path[0] === 'name') next.name = 'Escribe un nombre (hasta 200 caracteres).';
      }
      setErrors(next);
      return;
    }
    setErrors({});
    create.mutate(parsed.data, { onSuccess: onCreated });
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label="Nuevo festivo">
      {create.error ? <ProblemMessage error={create.error} /> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="holiday-date" label="Fecha" error={errors.date}>
          <input id="holiday-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} className={INPUT} {...describedBy('holiday-date', errors.date)} />
        </Field>
        <Field id="holiday-name" label="Nombre" error={errors.name}>
          <input id="holiday-name" value={name} onChange={(event) => setName(event.target.value)} className={INPUT} {...describedBy('holiday-name', errors.name)} />
        </Field>
      </div>
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" disabled={create.isPending} className={PRIMARY}>
          Añadir festivo
        </button>
      </ModalFooter>
    </form>
  );
}
